import React, { useRef, useState } from 'react';
import { Minus, Plus, ScanSearch } from 'lucide-react';
import { CardData } from '../types';
import { MiniMap } from './MiniMap';

type MiniMapHUDProps = {
  cards: CardData[];
  pan: { x: number; y: number };
  setPan: (pan: { x: number; y: number } | ((prev: { x: number; y: number }) => { x: number; y: number })) => void;
  scale: number;
  setScale: (scale: number) => void;
  canvasRef: React.RefObject<HTMLDivElement>;
  isDark: boolean;
  width: number;
  height: number;
  criticalStageId?: string | null;
  pendingActionCardIds?: string[];
  wasteZoneCardIds?: string[];
  onZoomIn: () => void;
  onZoomOut: () => void;
};

export const MiniMapHUD: React.FC<MiniMapHUDProps> = ({
  cards,
  pan,
  setPan,
  scale,
  setScale,
  canvasRef,
  isDark,
  width,
  height,
  criticalStageId = null,
  pendingActionCardIds = [],
  wasteZoneCardIds = [],
  onZoomIn,
  onZoomOut,
}) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const accent = '#22d3ee';
  const zoomPct = Math.round(scale * 100);

  const panelBg = isDark ? 'rgba(10,15,30,0.84)' : 'rgba(248,250,252,0.90)';
  const panelBorder = isDark ? `${accent}33` : `${accent}44`;
  const textColor = isDark ? '#94a3b8' : '#475569';
  const btnBorder = isDark ? 'rgba(51,65,85,0.7)' : 'rgba(203,213,225,0.9)';
  const btnHoverBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';
  const divider = isDark ? 'rgba(51,65,85,0.6)' : 'rgba(226,232,240,0.9)';

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 220);
  };

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {/* Expanded panel — floats above the pill */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        pointerEvents: open ? 'auto' : 'none',
        opacity: open ? 1 : 0,
        transform: open ? 'translateY(0)' : 'translateY(6px)',
        transition: 'opacity 160ms ease, transform 160ms ease',
        background: panelBg,
        border: `1px solid ${panelBorder}`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        boxShadow: isDark
          ? `0 8px 32px rgba(0,0,0,0.55), 0 0 0 1px ${accent}11`
          : `0 8px 24px rgba(0,0,0,0.13), 0 0 0 1px ${accent}22`,
        borderRadius: 6,
        overflow: 'hidden',
        minWidth: width,
      }}>
        {/* Header bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '5px 8px 4px',
          borderBottom: `1px solid ${divider}`,
        }}>
          <span style={{
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: accent,
          }}>Minimap</span>
          <span style={{
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            fontSize: 9,
            fontWeight: 600,
            color: textColor,
            letterSpacing: '0.06em',
          }}>{zoomPct}%</span>
        </div>

        {/* MiniMap canvas */}
        <div style={{ padding: '4px 4px 2px' }}>
          <MiniMap
            cards={cards}
            pan={pan}
            setPan={setPan}
            scale={scale}
            setScale={setScale}
            canvasRef={canvasRef}
            visible={true}
            isDark={isDark}
            chromeStyle="cad"
            width={width - 8}
            height={height}
            criticalStageId={criticalStageId}
            pendingActionCardIds={pendingActionCardIds}
            wasteZoneCardIds={wasteZoneCardIds}
          />
        </div>

        {/* Zoom controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 6px 6px',
          borderTop: `1px solid ${divider}`,
        }}>
          <button
            type="button"
            onClick={onZoomOut}
            title="Zoom −"
            style={{
              flex: 1, height: 22,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent',
              border: `1px solid ${btnBorder}`,
              borderRadius: 3,
              cursor: 'pointer',
              color: textColor,
              transition: 'background 120ms',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = btnHoverBg)}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <Minus size={11} strokeWidth={2} />
          </button>
          <span style={{
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            fontSize: 9,
            fontWeight: 700,
            color: accent,
            letterSpacing: '0.06em',
            minWidth: 28,
            textAlign: 'center',
          }}>{zoomPct}%</span>
          <button
            type="button"
            onClick={onZoomIn}
            title="Zoom +"
            style={{
              flex: 1, height: 22,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent',
              border: `1px solid ${btnBorder}`,
              borderRadius: 3,
              cursor: 'pointer',
              color: textColor,
              transition: 'background 120ms',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = btnHoverBg)}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <Plus size={11} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Collapsed pill — hidden when panel is open */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 28,
        paddingLeft: 8,
        paddingRight: 10,
        borderRadius: 9999,
        background: isDark ? 'rgba(15,23,42,0.55)' : 'rgba(248,250,252,0.60)',
        border: `1.5px solid ${accent}44`,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        boxShadow: `0 0 0 1px ${accent}1a, 0 2px 8px rgba(0,0,0,0.16)`,
        transition: 'opacity 150ms, transform 150ms',
        opacity: open ? 0 : 1,
        pointerEvents: open ? 'none' : 'auto',
        transform: open ? 'scale(0.92)' : 'scale(1)',
      }}>
        <ScanSearch size={12} style={{ color: accent, flexShrink: 0 }} strokeWidth={2} />
        <span style={{
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.06em',
          color: accent,
          lineHeight: 1,
        }}>
          {zoomPct}%
        </span>
      </div>
    </div>
  );
};
